import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ShieldCheck, Globe, CheckSquare, Sparkles, ChevronRight, X, ListChecks } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const ICON = { tour: Sparkles, "2fa": ShieldCheck, website: Globe, approval: CheckSquare };
const CONFETTI_COLORS = ["#A3E635", "#ffffff", "#84cc16", "#d9f99d"];

const Confetti = () => {
  const pieces = useMemo(() => Array.from({ length: 30 }).map((_, i) => ({
    left: Math.random() * 100,
    dx: Math.random() * 90 - 45,
    rot: Math.random() * 720 - 360,
    delay: Math.random() * 0.25,
    dur: 1.2 + Math.random() * 0.5,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    w: 6 + Math.random() * 4,
    h: 8 + Math.random() * 6,
  })), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10" data-testid="checklist-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span key={i} className="kewag-confetti-piece" style={{
          left: `${p.left}%`, width: p.w, height: p.h, background: p.color,
          animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`,
          "--dx": `${p.dx}px`, "--rot": `${p.rot}deg`,
        }} />
      ))}
    </div>
  );
};

// Wiederverwendbare "Erste Schritte"-Checkliste. variant="card" (Overview) oder "tour" (im Rundgang-Modal).
export default function GettingStarted({ items = [], accent = "#A3E635", onGo, onDismiss, onCelebrated, variant = "card" }) {
  const { t } = useI18n();
  const total = items.length;
  const done = items.filter((i) => i.done).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const allDone = total > 0 && done === total;

  const [confetti, setConfetti] = useState(false);
  const firedRef = useRef(false);
  useEffect(() => {
    if (allDone && !firedRef.current) {
      firedRef.current = true;
      setConfetti(true);
      onCelebrated?.();
      const tmr = setTimeout(() => setConfetti(false), 1800);
      return () => clearTimeout(tmr);
    }
  }, [allDone, onCelebrated]);

  const wrap = variant === "card"
    ? "relative overflow-hidden bg-[#121217] border border-white/10 rounded-2xl p-5"
    : "relative overflow-hidden";

  return (
    <div className={wrap} data-testid="getting-started">
      {confetti && <Confetti />}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}>
            <ListChecks className="w-5 h-5" />
          </span>
          <div>
            <div className="font-bold text-[15px]">{t("Erste Schritte")}</div>
            <div className="text-xs text-gray-500" data-testid="gs-progress">{done} {t("von")} {total} {t("erledigt")}</div>
          </div>
        </div>
        {variant === "card" && onDismiss && (
          <button onClick={onDismiss} data-testid="getting-started-dismiss" aria-label={t("Später")}
            className="p-1.5 -mr-1 text-gray-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        )}
      </div>

      <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: accent }} />
      </div>

      {allDone ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-200 font-medium" data-testid="getting-started-done">
          <Sparkles className="w-4 h-4" style={{ color: accent }} /> {t("Geschafft! Sie haben alle ersten Schritte erledigt.")}
        </div>
      ) : (
        <ul className="mt-4 space-y-2.5">
          {items.map((it) => {
            const Icon = ICON[it.key] || Check;
            return (
              <li key={it.key} className="flex items-center gap-3" data-testid={`gs-item-${it.key}`}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 border"
                  style={it.done ? { background: `${accent}1A`, borderColor: `${accent}66`, color: accent } : { borderColor: "rgba(255,255,255,0.15)", color: "#9ca3af" }}>
                  {it.done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </span>
                <span className={`flex-1 text-sm ${it.done ? "text-gray-500 line-through" : "text-gray-200 font-medium"}`}>{t(it.label)}</span>
                {!it.done && it.to && (
                  <button onClick={() => onGo?.(it.to)} data-testid={`gs-cta-${it.key}`}
                    className="shrink-0 flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors hover:bg-white/5"
                    style={{ color: accent, borderColor: `${accent}55` }}>
                    {t(it.cta)} <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
